import { useState, useCallback, useRef, useEffect, type MouseEvent } from 'react';

interface UseResizableSplitterOptions {
  direction: 'vertical' | 'horizontal';
  initialSize: number;
  minSize: number;
  maxSize: number;
  invertDelta?: boolean;
}

/**
 * Hook for drag-resizable splitter panels.
 * Returns current size and props to spread onto the splitter element.
 *
 * For vertical: resizes height (drag up/down).
 * For horizontal: resizes width (drag left/right).
 */
export function useResizableSplitter({
  direction,
  initialSize,
  minSize,
  maxSize,
  invertDelta = false,
}: UseResizableSplitterOptions) {
  const [size, setSize] = useState(initialSize);
  const dragging = useRef(false);
  const startPos = useRef(0);
  const startSize = useRef(0);

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      startPos.current = direction === 'vertical' ? e.clientY : e.clientX;
      startSize.current = size;
    },
    [direction, size],
  );

  useEffect(() => {
    const onMouseMove = (e: globalThis.MouseEvent) => {
      if (!dragging.current) return;
      const current = direction === 'vertical' ? e.clientY : e.clientX;
      // Vertical (south panel): drag up = increase size → startPos - current
      // Horizontal (left panel): drag right = increase size → current - startPos
      const rawDelta = direction === 'vertical'
        ? startPos.current - current
        : current - startPos.current;
      const delta = invertDelta ? -rawDelta : rawDelta;
      const next = Math.max(minSize, Math.min(maxSize, startSize.current + delta));
      setSize(next);
    };

    const onMouseUp = () => {
      dragging.current = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [direction, invertDelta, minSize, maxSize]);

  return {
    size,
    setSize,
    splitterProps: {
      onMouseDown,
      role: 'separator' as const,
      'aria-orientation': direction as 'vertical' | 'horizontal',
    },
  };
}
